import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { SearchableSelect } from '../components/SearchableSelect';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Ship, Plus, Trash2, Anchor, FileText, ArrowLeft, Loader2, CheckCircle2,
  RotateCcw, Fish, Receipt, TrendingUp, Save, Send, BarChart3, Upload, FileSpreadsheet,
  Printer, Download,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import FishingTripPrint from '../components/invoice/FishingTripPrint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => (Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

const emptyInvoiceLine = () => ({ fish_name: '', calibre_name: '', price: '', cost: '', weight: '' });
const emptyExpenseLine = () => ({ description: '', debit_account_code: '', credit_account_code: '', amount: '' });

const FishingTripPage = () => {
  const { currentOrg, user, isSuperAdmin } = useAuth();
  const [view, setView] = useState('list'); // list | form
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Reference data
  const [ships, setShips] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesAccounts, setSalesAccounts] = useState([]);
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);
  const [supplierAccounts, setSupplierAccounts] = useState([]);
  const [expenseDebitAccounts, setExpenseDebitAccounts] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [calibres, setCalibres] = useState([]);
  const [fishItems, setFishItems] = useState([]);

  // Form state
  const [editingId, setEditingId] = useState(null);
  const [header, setHeader] = useState({});
  const [invoiceLines, setInvoiceLines] = useState([emptyInvoiceLine()]);
  const [expenseLines, setExpenseLines] = useState([emptyExpenseLine()]);
  const [tripNumber, setTripNumber] = useState('');

  // Add ship dialog
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [newShipName, setNewShipName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Report + Import
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportShip, setReportShip] = useState('all');
  const [reportCurrency, setReportCurrency] = useState('XOF');
  const [reportTripIds, setReportTripIds] = useState([]); // empty = all trips
  const [tripPickerOpen, setTripPickerOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importInputRef = React.useRef(null);

  const canManage = isSuperAdmin?.() || ['admin', 'accountant'].includes(user?.role);
  const canDelete = isSuperAdmin?.() || user?.role === 'admin';

  useEffect(() => {
    if (currentOrg) {
      fetchTrips();
      fetchReferenceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg]);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fishing-trips`, { params: { organization_id: currentOrg.id } });
      setTrips(res.data || []);
      setSelectedIds([]);
    } catch (e) {
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const acctByPrefix = (prefix) => axios.get(`${API}/fishing-trips/accounts`, { params: { organization_id: currentOrg.id, prefix } });
      const [shipsRes, custRes, salesRes, purchRes, suppRes, expRes, cashRes, calRes, fishRes] = await Promise.all([
        axios.get(`${API}/fishing-trips/ships`, { params: { organization_id: currentOrg.id } }),
        acctByPrefix('4111'),
        acctByPrefix('7011'),
        acctByPrefix('6011'),
        acctByPrefix('4011'),
        acctByPrefix('6311'),
        acctByPrefix('5311'),
        axios.get(`${API}/calibres`, { params: { organization_id: currentOrg.id } }),
        axios.get(`${API}/inventory`, { params: { organization_id: currentOrg.id, page_size: 1000 } }),
      ]);
      setShips(shipsRes.data || []);
      setCustomers(custRes.data || []);
      setSalesAccounts(salesRes.data || []);
      setPurchaseAccounts(purchRes.data || []);
      setSupplierAccounts(suppRes.data || []);
      setExpenseDebitAccounts(expRes.data || []);
      setCashAccounts(cashRes.data || []);
      setCalibres(calRes.data || []);
      setFishItems((fishRes.data.items || []).filter(i => !i.is_voided));
    } catch (e) {
      console.error('Reference data error', e);
    }
  };

  const startNewTrip = async () => {
    setEditingId(null);
    let num = '';
    try {
      const res = await axios.get(`${API}/fishing-trips/next-number`, { params: { organization_id: currentOrg.id } });
      num = res.data.trip_number;
    } catch (e) { /* ignore */ }
    setTripNumber(num);
    setHeader({
      date: new Date().toISOString().slice(0, 10),
      ship_account_code: ships[0]?.code || '',
      customer_account_code: customers[0]?.code || '',
      sales_account_code: salesAccounts[0]?.code || '',
      purchase_account_code: purchaseAccounts[0]?.code || '',
      supplier_account_code: supplierAccounts[0]?.code || '',
      is_paid: false,
      cash_account_code: cashAccounts[0]?.code || '',
      is_supplier_paid: false,
      supplier_cash_account_code: cashAccounts[0]?.code || '',
      notes: '',
    });
    const defDebit = expenseDebitAccounts[0]?.code || '';
    const defCredit = cashAccounts[0]?.code || '';
    setInvoiceLines([emptyInvoiceLine()]);
    setExpenseLines([{ description: '', debit_account_code: defDebit, credit_account_code: defCredit, amount: '' }]);
    setView('form');
  };

  const editTrip = (trip) => {
    setEditingId(trip.id);
    setTripNumber(trip.trip_number);
    setHeader({
      date: trip.date,
      ship_account_code: trip.ship_account_code || '',
      customer_account_code: trip.customer_account_code || '',
      sales_account_code: trip.sales_account_code || '',
      purchase_account_code: trip.purchase_account_code || purchaseAccounts[0]?.code || '',
      supplier_account_code: trip.supplier_account_code || supplierAccounts[0]?.code || '',
      is_paid: !!trip.is_paid,
      cash_account_code: trip.cash_account_code || cashAccounts[0]?.code || '',
      is_supplier_paid: !!trip.is_supplier_paid,
      supplier_cash_account_code: trip.supplier_cash_account_code || cashAccounts[0]?.code || '',
      notes: trip.notes || '',
    });
    setInvoiceLines(trip.invoice_lines?.length
      ? trip.invoice_lines.map(l => ({ fish_name: l.fish_name, calibre_name: l.calibre_name || '', price: l.price ?? '', cost: l.cost ?? '', weight: l.weight ?? '' }))
      : [emptyInvoiceLine()]);
    setExpenseLines(trip.expense_lines?.length
      ? trip.expense_lines.map(l => ({ description: l.description, debit_account_code: l.debit_account_code || '', credit_account_code: l.credit_account_code || '', amount: l.amount ?? '' }))
      : [{ description: '', debit_account_code: expenseDebitAccounts[0]?.code || '', credit_account_code: cashAccounts[0]?.code || '', amount: '' }]);
    setView('form');
  };

  // Line helpers
  const updateInvoiceLine = (idx, field, value) => {
    setInvoiceLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };
  const updateExpenseLine = (idx, field, value) => {
    setExpenseLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };
  const addInvoiceLine = () => setInvoiceLines(prev => [...prev, emptyInvoiceLine()]);
  const addExpenseLine = () => setExpenseLines(prev => [...prev, { description: '', debit_account_code: expenseDebitAccounts[0]?.code || '', credit_account_code: cashAccounts[0]?.code || '', amount: '' }]);
  const removeInvoiceLine = (idx) => setInvoiceLines(prev => prev.filter((_, i) => i !== idx));
  const removeExpenseLine = (idx) => setExpenseLines(prev => prev.filter((_, i) => i !== idx));

  const seedAllFish = () => {
    if (!fishItems.length) {
      toast.error('No fish types found. Import fish types in Inventory first.');
      return;
    }
    const existing = invoiceLines.filter(l => l.fish_name || l.price || l.weight);
    const seeded = fishItems.map(f => ({ fish_name: f.name, calibre_name: '', price: '', cost: '', weight: '' }));
    setInvoiceLines([...existing, ...seeded]);
    toast.success(`Added ${seeded.length} fish types`);
  };

  const totalFacture = invoiceLines.reduce((s, l) => s + (Number(l.price) || 0) * (Number(l.weight) || 0), 0);
  const totalCost = invoiceLines.reduce((s, l) => s + (Number(l.cost) || 0) * (Number(l.weight) || 0), 0);
  const totalDepenses = expenseLines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const result = totalFacture - totalCost - totalDepenses;

  const buildPayload = () => {
    const shipObj = ships.find(s => s.code === header.ship_account_code);
    const custObj = customers.find(c => c.code === header.customer_account_code);
    const salesObj = salesAccounts.find(c => c.code === header.sales_account_code);
    const purchObj = purchaseAccounts.find(c => c.code === header.purchase_account_code);
    const suppObj = supplierAccounts.find(c => c.code === header.supplier_account_code);
    const cashObj = cashAccounts.find(c => c.code === header.cash_account_code);
    const supCashObj = cashAccounts.find(c => c.code === header.supplier_cash_account_code);
    return {
      organization_id: currentOrg.id,
      date: header.date,
      ship_account_code: header.ship_account_code || null,
      ship_account_name: shipObj?.name || null,
      customer_account_code: header.customer_account_code || null,
      customer_account_name: custObj?.name || null,
      sales_account_code: header.sales_account_code || null,
      sales_account_name: salesObj?.name || null,
      purchase_account_code: header.purchase_account_code || null,
      purchase_account_name: purchObj?.name || null,
      supplier_account_code: header.supplier_account_code || null,
      supplier_account_name: suppObj?.name || null,
      is_paid: !!header.is_paid,
      cash_account_code: header.cash_account_code || null,
      cash_account_name: cashObj?.name || null,
      is_supplier_paid: !!header.is_supplier_paid,
      supplier_cash_account_code: header.supplier_cash_account_code || null,
      supplier_cash_account_name: supCashObj?.name || null,
      notes: header.notes || null,
      invoice_lines: invoiceLines
        .filter(l => (Number(l.weight) || 0) > 0 && l.fish_name)
        .map(l => ({
          fish_name: l.fish_name,
          calibre_name: l.calibre_name || null,
          price: Number(l.price) || 0,
          cost: Number(l.cost) || 0,
          weight: Number(l.weight) || 0,
        })),
      expense_lines: expenseLines
        .filter(l => (Number(l.amount) || 0) > 0 && l.description)
        .map(l => ({
          description: l.description,
          debit_account_code: l.debit_account_code || null,
          debit_account_name: expenseDebitAccounts.find(a => a.code === l.debit_account_code)?.name || null,
          credit_account_code: l.credit_account_code || null,
          credit_account_name: cashAccounts.find(a => a.code === l.credit_account_code)?.name || null,
          amount: Number(l.amount) || 0,
        })),
    };
  };

  const saveTrip = async ({ thenPost = false } = {}) => {
    if (!header.date) { toast.error('Date is required'); return null; }
    if (!header.ship_account_code) { toast.error('Please select a ship'); return null; }
    setSaving(true);
    try {
      const payload = buildPayload();
      let trip;
      if (editingId) {
        const res = await axios.put(`${API}/fishing-trips/${editingId}`, payload);
        trip = res.data;
      } else {
        const res = await axios.post(`${API}/fishing-trips`, payload);
        trip = res.data;
        setEditingId(trip.id);
      }
      if (thenPost) {
        const posted = await axios.post(`${API}/fishing-trips/${trip.id}/post`);
        toast.success(`Trip ${posted.data.trip_number} posted`);
      } else {
        toast.success(`Trip ${trip.trip_number} saved`);
      }
      await fetchTrips();
      setView('list');
      return trip;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save trip');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const postExisting = async (trip) => {
    try {
      await axios.post(`${API}/fishing-trips/${trip.id}/post`);
      toast.success(`Trip ${trip.trip_number} posted`);
      fetchTrips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post trip');
    }
  };

  const unpostExisting = async (trip) => {
    try {
      await axios.post(`${API}/fishing-trips/${trip.id}/unpost`);
      toast.success(`Trip ${trip.trip_number} unposted`);
      fetchTrips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unpost trip');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/fishing-trips/${deleteTarget.id}`);
      toast.success('Trip deleted');
      setDeleteTarget(null);
      fetchTrips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete trip');
    }
  };

  // ============ MULTI-SELECT DELETE ============
  const draftTrips = trips.filter(t => t.status !== 'posted');
  const allDraftSelected = draftTrips.length > 0 && draftTrips.every(t => selectedIds.includes(t.id));
  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () =>
    setSelectedIds(allDraftSelected ? [] : draftTrips.map(t => t.id));

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    try {
      const res = await axios.post(`${API}/fishing-trips/bulk-delete`, {
        organization_id: currentOrg.id,
        trip_ids: selectedIds,
      });
      const { deleted = 0, skipped_posted = 0 } = res.data || {};
      toast.success(`${deleted} trip${deleted !== 1 ? 's' : ''} deleted${skipped_posted ? ` · ${skipped_posted} posted skipped` : ''}`);
      setBulkDeleteOpen(false);
      setSelectedIds([]);
      fetchTrips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete trips');
    }
  };

  // ============ PRINT (Invoice / Receipt) ============
  const printDoc = (trip, action) => {
    const printer = FishingTripPrint({ trip, organization: currentOrg });
    const needsReceipt = action === 'printReceipt' || action === 'downloadReceiptPdf';
    if (needsReceipt && !trip.is_paid) {
      toast.warning('This trip is not marked as Paid. Receipt is available only for paid trips.');
      return;
    }
    if ((trip.total_facture || 0) <= 0) {
      toast.warning('No invoice amount to print for this trip.');
      return;
    }
    printer[action]();
  };

  const createShip = async () => {
    if (!newShipName.trim()) { toast.error('Ship name required'); return; }
    try {
      const res = await axios.post(`${API}/fishing-trips/ships`, { organization_id: currentOrg.id, name: newShipName.trim() });
      toast.success(`Ship ${res.data.name} created (${res.data.code})`);
      setNewShipName('');
      setShipDialogOpen(false);
      const shipsRes = await axios.get(`${API}/fishing-trips/ships`, { params: { organization_id: currentOrg.id } });
      setShips(shipsRes.data || []);
      setHeader(h => ({ ...h, ship_account_code: res.data.code }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ship');
    }
  };

  // ============ REPORT VIEW ============
  const fetchReport = async (opts = {}) => {
    setReportLoading(true);
    try {
      const ship = opts.ship ?? reportShip;
      const ids = opts.tripIds ?? reportTripIds;
      const params = { organization_id: currentOrg.id };
      if (ship && ship !== 'all') params.ship = ship;
      if (ids && ids.length) params.trip_ids = ids.join(',');
      const res = await axios.get(`${API}/fishing-trips/summary`, { params });
      setReport(res.data);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setReportLoading(false);
    }
  };

  const openReport = async () => {
    setView('report');
    setReportShip('all');
    setReportCurrency('XOF');
    setReportTripIds([]);
    fetchReport({ ship: 'all', tripIds: [] });
  };

  const onReportShipChange = (v) => { setReportShip(v); fetchReport({ ship: v }); };
  const toggleReportTrip = (id) => {
    const next = reportTripIds.includes(id) ? reportTripIds.filter(x => x !== id) : [...reportTripIds, id];
    setReportTripIds(next);
    fetchReport({ tripIds: next });
  };
  const clearReportTrips = () => { setReportTripIds([]); fetchReport({ tripIds: [] }); };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const payload = new FormData();
      payload.append('file', importFile);
      payload.append('organization_id', currentOrg.id);
      const res = await axios.post(`${API}/fishing-trips/import`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      toast.success(`Imported ${res.data.created} trip(s)`);
      fetchTrips();
    } catch (e) {
      setImportResult({ error: e.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  // ============ LIST VIEW ============
  if (view === 'list') {
    return (
      <div className="space-y-4 lg:space-y-6" data-testid="fishing-trips-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-primary/15 text-primary">
              <Ship className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl lg:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Ocean Fishing Trip
              </h1>
              <p className="text-sm text-muted-foreground">Trip invoicing &amp; expenses per voyage</p>
            </div>
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2">
              {canDelete && selectedIds.length > 0 && (
                <Button variant="destructive" onClick={() => setBulkDeleteOpen(true)} data-testid="bulk-delete-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Selected ({selectedIds.length})
                </Button>
              )}
              <Button variant="outline" onClick={openReport} data-testid="report-btn">
                <BarChart3 className="w-4 h-4 mr-2" /> Report
              </Button>
              <Button variant="outline" onClick={() => { setImportDialogOpen(true); setImportFile(null); setImportResult(null); }} data-testid="import-trips-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Import Excel
              </Button>
              <Button className="btn-glow" onClick={startNewTrip} data-testid="new-trip-btn">
                <Plus className="w-4 h-4 mr-2" /> New Trip
              </Button>
            </div>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 flex items-center justify-center text-muted-foreground">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...
              </div>
            ) : trips.length === 0 ? (
              <div className="p-12 text-center" data-testid="trips-empty">
                <Anchor className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
                <p className="font-medium">No trips yet</p>
                <p className="text-sm text-muted-foreground mb-4">Create your first fishing trip transaction.</p>
                {canManage && (
                  <Button variant="outline" onClick={startNewTrip}><Plus className="w-4 h-4 mr-2" /> New Trip</Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table w-full">
                  <thead>
                    <tr>
                      {canDelete && (
                        <th className="w-10 text-center">
                          <Checkbox
                            checked={allDraftSelected}
                            onCheckedChange={toggleSelectAll}
                            disabled={draftTrips.length === 0}
                            aria-label="Select all draft trips"
                            data-testid="select-all-trips"
                          />
                        </th>
                      )}
                      <th>Trip No</th>
                      <th>Date</th>
                      <th>Ship</th>
                      <th className="text-right">Facture (XOF)</th>
                      <th className="text-right">Dépenses (XOF)</th>
                      <th className="text-right">Résultat (XOF)</th>
                      <th className="text-center">Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trips.map(t => (
                      <tr key={t.id} data-testid={`trip-row-${t.id}`}>
                        {canDelete && (
                          <td className="text-center">
                            {t.status !== 'posted' ? (
                              <Checkbox
                                checked={selectedIds.includes(t.id)}
                                onCheckedChange={() => toggleSelect(t.id)}
                                aria-label={`Select ${t.trip_number}`}
                                data-testid={`select-trip-${t.id}`}
                              />
                            ) : null}
                          </td>
                        )}
                        <td className="font-mono font-medium">{t.trip_number}</td>
                        <td className="text-muted-foreground text-sm">{t.date}</td>
                        <td>{t.ship_account_name || t.ship_account_code || '-'}</td>
                        <td className="text-right font-mono">{fmt(t.total_facture)}</td>
                        <td className="text-right font-mono">{fmt(t.total_depenses)}</td>
                        <td className={`text-right font-mono font-semibold ${t.result >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{fmt(t.result)}</td>
                        <td className="text-center">
                          <Badge variant={t.status === 'posted' ? 'default' : 'secondary'} className={t.status === 'posted' ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' : ''}>
                            {t.status}
                          </Badge>
                        </td>
                        <td>
                          <div className="flex gap-1 justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" title="Print" data-testid={`print-trip-${t.id}`}>
                                  <Printer className="w-3.5 h-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuLabel className="flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Invoice (Facture)</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => printDoc(t, 'printInvoice')} data-testid={`print-invoice-${t.id}`}>
                                  <Printer className="w-3.5 h-3.5 mr-2" /> Print Invoice
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => printDoc(t, 'downloadInvoicePdf')} data-testid={`download-invoice-${t.id}`}>
                                  <Download className="w-3.5 h-3.5 mr-2" /> Download PDF
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="flex items-center gap-2"><Receipt className="w-3.5 h-3.5" /> Receipt (Reçu)</DropdownMenuLabel>
                                <DropdownMenuItem disabled={!t.is_paid} onClick={() => printDoc(t, 'printReceipt')} data-testid={`print-receipt-${t.id}`}>
                                  <Printer className="w-3.5 h-3.5 mr-2" /> Print Receipt
                                </DropdownMenuItem>
                                <DropdownMenuItem disabled={!t.is_paid} onClick={() => printDoc(t, 'downloadReceiptPdf')} data-testid={`download-receipt-${t.id}`}>
                                  <Download className="w-3.5 h-3.5 mr-2" /> Download PDF
                                </DropdownMenuItem>
                                {!t.is_paid && (
                                  <p className="px-2 py-1 text-[11px] text-muted-foreground">Receipt requires a Paid trip.</p>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button variant="ghost" size="sm" onClick={() => editTrip(t)} title={t.status === 'posted' ? 'View' : 'Edit'} data-testid={`edit-trip-${t.id}`}>
                              <FileText className="w-3.5 h-3.5" />
                            </Button>
                            {canManage && t.status !== 'posted' && (
                              <Button variant="ghost" size="sm" className="text-emerald-500" onClick={() => postExisting(t)} title="Post" data-testid={`post-trip-${t.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {canDelete && t.status === 'posted' && (
                              <Button variant="ghost" size="sm" className="text-amber-500" onClick={() => unpostExisting(t)} title="Unpost">
                                <RotateCcw className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {canDelete && t.status !== 'posted' && (
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteTarget(t)} title="Delete" data-testid={`delete-trip-${t.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete trip?</AlertDialogTitle>
              <AlertDialogDescription>
                Permanently delete draft trip "{deleteTarget?.trip_number}"? This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-trip-btn">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {selectedIds.length} selected trip{selectedIds.length !== 1 ? 's' : ''}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete {selectedIds.length} draft trip{selectedIds.length !== 1 ? 's' : ''}. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleBulkDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-bulk-delete-btn">Delete Selected</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Import Excel Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={(o) => { setImportDialogOpen(o); if (!o) { setImportFile(null); setImportResult(null); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-primary" /> Import Trip Sheets</DialogTitle>
              <DialogDescription>
                Upload the OCEAN workbook (.xlsx). Each trip sheet becomes a <strong>draft</strong> trip
                (fish sales + expenses) using default accounts. Only priced fish rows are imported. Review &amp; post afterwards.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <input ref={importInputRef} type="file" accept=".xlsx,.xls" className="hidden"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }} data-testid="import-file-input" />
              <Button variant="outline" className="w-full" onClick={() => importInputRef.current?.click()} data-testid="import-select-file-btn">
                <Upload className="w-4 h-4 mr-2" /> {importFile ? importFile.name : 'Select Excel File'}
              </Button>
              {importResult && !importResult.error && (
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm max-h-48 overflow-y-auto" data-testid="import-result">
                  <p className="font-medium text-emerald-500">{importResult.message} — {importResult.created} trip(s)</p>
                  {(importResult.trips || []).map((t, i) => (
                    <p key={i} className="text-muted-foreground text-xs mt-1">{t.trip_number} · {t.sheet} · F {fmt(t.facture)} / D {fmt(t.depenses)}</p>
                  ))}
                  {(importResult.warnings || []).length > 0 && (
                    <p className="text-amber-500 text-xs mt-2">Warnings: {importResult.warnings.join('; ')}</p>
                  )}
                </div>
              )}
              {importResult?.error && (
                <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-500">{importResult.error}</div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Close</Button>
              <Button onClick={handleImport} disabled={!importFile || importing} data-testid="do-import-btn">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />} Import
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============ REPORT VIEW ============
  if (view === 'report') {
    const t = report?.totals || { facture: 0, depenses: 0, result: 0 };
    const rate = report?.exchange_rate || 655.957;
    const conv = (xof) => (reportCurrency === 'EUR' ? (Number(xof) || 0) / rate : (Number(xof) || 0));
    const money = (xof) => fmt(conv(xof));
    const cur = reportCurrency;
    return (
      <div className="space-y-4 lg:space-y-6" data-testid="fishing-trips-report">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setView('list')} data-testid="report-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-primary/15 text-primary"><BarChart3 className="w-6 h-6" /></div>
            <div>
              <h1 className="text-xl lg:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Trip Results Report</h1>
              <p className="text-sm text-muted-foreground">Facture · Dépenses · Résultat per trip ({cur})</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-3">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Ship</Label>
                <div className="min-w-[200px]">
                  <SearchableSelect
                    testid="report-ship-filter"
                    value={reportShip}
                    onChange={onReportShipChange}
                    placeholder="All Ships"
                    options={[{ value: 'all', label: 'All Ships' }, ...ships.map(s => ({ value: s.code, label: `${s.name} (${s.code})` }))]}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Trips</Label>
                <Popover open={tripPickerOpen} onOpenChange={setTripPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="min-w-[180px] justify-between font-normal" data-testid="report-trip-filter">
                      {reportTripIds.length ? `${reportTripIds.length} selected` : 'All Trips'}
                      <BarChart3 className="w-3.5 h-3.5 opacity-50 ml-2" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-0" align="start">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
                      <span className="text-sm font-medium">Select trips</span>
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearReportTrips} data-testid="report-trip-clear">All / Clear</Button>
                    </div>
                    <div className="max-h-64 overflow-y-auto py-1">
                      {trips.map(tr => (
                        <label key={tr.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-accent" data-testid={`report-trip-opt-${tr.id}`}>
                          <Checkbox checked={reportTripIds.includes(tr.id)} onCheckedChange={() => toggleReportTrip(tr.id)} />
                          <span className="font-mono">{tr.trip_number}</span>
                          <span className="text-muted-foreground text-xs ml-auto">{tr.date}</span>
                        </label>
                      ))}
                      {trips.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">No trips.</p>}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Currency</Label>
                <div className="flex rounded-md border border-border overflow-hidden" data-testid="report-currency-toggle">
                  {['XOF', 'EUR'].map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setReportCurrency(c)}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${reportCurrency === c ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground hover:bg-accent'}`}
                      data-testid={`report-currency-${c}`}
                    >
                      {c === 'XOF' ? 'XOF' : 'Euro (€)'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card><CardContent className="py-4"><p className="text-xs text-muted-foreground uppercase">Total Facture ({cur})</p><p className="text-xl font-bold font-mono" data-testid="report-total-facture">{money(t.facture)}</p></CardContent></Card>
          <Card><CardContent className="py-4"><p className="text-xs text-muted-foreground uppercase">Total Dépenses ({cur})</p><p className="text-xl font-bold font-mono" data-testid="report-total-depenses">{money(t.depenses)}</p></CardContent></Card>
          <Card className="border-primary/30"><CardContent className="py-4"><p className="text-xs text-muted-foreground uppercase">Total Résultat ({cur})</p><p className={`text-xl font-bold font-mono ${t.result >= 0 ? 'text-emerald-500' : 'text-red-500'}`} data-testid="report-total-result">{money(t.result)}</p></CardContent></Card>
        </div>

        <Card>
          <CardContent className="p-0">
            {reportLoading ? (
              <div className="p-8 flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table w-full">
                  <thead>
                    <tr>
                      <th>Trip</th>
                      <th>Date</th>
                      <th>Ship</th>
                      <th className="text-right">Dépenses ({cur})</th>
                      <th className="text-right">Facture ({cur})</th>
                      <th className="text-right">Résultat ({cur})</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report?.trips || []).map((r) => (
                      <tr key={r.id} data-testid={`report-row-${r.id}`}>
                        <td className="font-mono font-medium">{r.trip_number}</td>
                        <td className="text-muted-foreground text-sm">{r.date}</td>
                        <td>{r.ship_account_name || r.ship_account_code || '-'}</td>
                        <td className="text-right font-mono">{money(r.total_depenses)}</td>
                        <td className="text-right font-mono">{money(r.total_facture)}</td>
                        <td className={`text-right font-mono font-semibold ${r.result >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{money(r.result)}</td>
                        <td className="text-center"><Badge variant={r.status === 'posted' ? 'default' : 'secondary'} className={r.status === 'posted' ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' : ''}>{r.status}</Badge></td>
                      </tr>
                    ))}
                    {(report?.trips || []).length === 0 && (
                      <tr><td colSpan={7} className="text-center text-muted-foreground py-8">No trips to report.</td></tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border font-bold">
                      <td colSpan={3} className="text-right pr-4">TOTAL</td>
                      <td className="text-right font-mono">{money(t.depenses)}</td>
                      <td className="text-right font-mono">{money(t.facture)}</td>
                      <td className={`text-right font-mono ${t.result >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{money(t.result)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============ FORM VIEW ============
  const isPosted = editingId && trips.find(t => t.id === editingId)?.status === 'posted';
  const readOnly = isPosted;

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="fishing-trip-form">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setView('list')} data-testid="back-to-list-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg lg:text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Ship className="w-5 h-5 text-primary" />
              {editingId ? 'Trip' : 'New Trip'} <span className="font-mono text-primary">{tripNumber}</span>
              {isPosted && <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30">posted</Badge>}
            </h1>
          </div>
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => saveTrip({ thenPost: false })} disabled={saving} data-testid="save-trip-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Draft
            </Button>
            <Button className="btn-glow" onClick={() => saveTrip({ thenPost: true })} disabled={saving} data-testid="save-post-trip-btn">
              <Send className="w-4 h-4 mr-2" /> Save &amp; Post
            </Button>
          </div>
        )}
      </div>

      {/* Header */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Trip Details</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Ship *</Label>
              <div className="flex gap-2">
                <Select value={header.ship_account_code} onValueChange={(v) => setHeader({ ...header, ship_account_code: v })} disabled={readOnly}>
                  <SelectTrigger data-testid="ship-select"><SelectValue placeholder="Select ship" /></SelectTrigger>
                  <SelectContent>
                    {ships.map(s => <SelectItem key={s.code} value={s.code}>{s.name} ({s.code})</SelectItem>)}
                  </SelectContent>
                </Select>
                {!readOnly && (
                  <Button variant="outline" size="icon" onClick={() => setShipDialogOpen(true)} title="Add ship" data-testid="add-ship-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Trip Number</Label>
              <Input value={tripNumber} disabled className="font-mono bg-muted/50" />
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={header.date || ''} onChange={(e) => setHeader({ ...header, date: e.target.value })} disabled={readOnly} data-testid="trip-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Customer (Débit 4111)</Label>
              <SearchableSelect
                testid="customer-select"
                disabled={readOnly}
                value={header.customer_account_code}
                onChange={(v) => setHeader({ ...header, customer_account_code: v })}
                placeholder="Search customer..."
                options={customers.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Sales (Crédit 7011)</Label>
              <SearchableSelect
                testid="sales-select"
                disabled={readOnly}
                value={header.sales_account_code}
                onChange={(v) => setHeader({ ...header, sales_account_code: v })}
                placeholder="Search sales account..."
                options={salesAccounts.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Purchase (Débit 6011)</Label>
              <SearchableSelect
                testid="purchase-select"
                disabled={readOnly}
                value={header.purchase_account_code}
                onChange={(v) => setHeader({ ...header, purchase_account_code: v })}
                placeholder="Search purchase account..."
                options={purchaseAccounts.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Supplier (Crédit 4011)</Label>
              <SearchableSelect
                testid="supplier-select"
                disabled={readOnly}
                value={header.supplier_account_code}
                onChange={(v) => setHeader({ ...header, supplier_account_code: v })}
                placeholder="Search supplier account..."
                options={supplierAccounts.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoicing */}
      <Card>
        <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2"><Fish className="w-4 h-4 text-primary" /> Invoicing (Facture)</CardTitle>
          {!readOnly && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={seedAllFish} data-testid="seed-fish-btn"><Fish className="w-3.5 h-3.5 mr-1.5" /> Seed all fish types</Button>
              <Button variant="outline" size="sm" onClick={addInvoiceLine} data-testid="add-invoice-line-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add line</Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-x-6 gap-y-3 px-4 py-3 border-b border-border/50 bg-muted/20 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
              <Checkbox
                checked={!!header.is_paid}
                onCheckedChange={(c) => setHeader({ ...header, is_paid: !!c })}
                disabled={readOnly}
                data-testid="trip-paid-checkbox"
              />
              Paid (cash received)
            </label>
            {header.is_paid && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Cash account (5311):</span>
                <div className="min-w-[240px]">
                  <SearchableSelect
                    size="sm"
                    disabled={readOnly}
                    value={header.cash_account_code}
                    onChange={(v) => setHeader({ ...header, cash_account_code: v })}
                    placeholder="Search cash account..."
                    testid="cash-select"
                    options={cashAccounts.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
                  />
                </div>
              </div>
            )}
            <span className="hidden sm:block h-5 w-px bg-border" />
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
              <Checkbox
                checked={!!header.is_supplier_paid}
                onCheckedChange={(c) => setHeader({ ...header, is_supplier_paid: !!c })}
                disabled={readOnly}
                data-testid="trip-supplier-paid-checkbox"
              />
              Paid (cash paid)
            </label>
            {header.is_supplier_paid && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Cash account (5311):</span>
                <div className="min-w-[240px]">
                  <SearchableSelect
                    size="sm"
                    disabled={readOnly}
                    value={header.supplier_cash_account_code}
                    onChange={(v) => setHeader({ ...header, supplier_cash_account_code: v })}
                    placeholder="Search cash account..."
                    testid="supplier-cash-select"
                    options={cashAccounts.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th className="w-8">#</th>
                  <th style={{ minWidth: 200 }}>Fish Type</th>
                  <th style={{ minWidth: 140 }}>Calibre</th>
                  <th className="text-right" style={{ minWidth: 110 }}>Prix/kg</th>
                  <th className="text-right" style={{ minWidth: 110 }}>Cout/kg</th>
                  <th className="text-right" style={{ minWidth: 110 }}>Poids (kg)</th>
                  <th className="text-right" style={{ minWidth: 130 }}>Total (XOF)</th>
                  {!readOnly && <th className="w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {invoiceLines.map((l, idx) => (
                  <tr key={idx} data-testid={`invoice-line-${idx}`}>
                    <td className="text-muted-foreground text-xs">{idx + 1}</td>
                    <td>
                      <SearchableSelect
                        size="sm"
                        allowCustom
                        disabled={readOnly}
                        value={l.fish_name}
                        onChange={(v) => updateInvoiceLine(idx, 'fish_name', v)}
                        placeholder="Fish type"
                        testid={`invoice-fish-${idx}`}
                        options={fishItems.map(f => ({ value: f.name, label: f.name }))}
                      />
                    </td>
                    <td>
                      <SearchableSelect
                        size="sm"
                        allowCustom
                        disabled={readOnly}
                        value={l.calibre_name}
                        onChange={(v) => updateInvoiceLine(idx, 'calibre_name', v)}
                        placeholder="Calibre"
                        testid={`invoice-calibre-${idx}`}
                        options={calibres.map(c => ({ value: c.name, label: c.name }))}
                      />
                    </td>
                    <td>
                      <Input type="number" value={l.price} placeholder="0" disabled={readOnly}
                        onChange={(e) => updateInvoiceLine(idx, 'price', e.target.value)} className="h-8 text-right" data-testid={`invoice-price-${idx}`} />
                    </td>
                    <td>
                      <Input type="number" value={l.cost} placeholder="0" disabled={readOnly}
                        onChange={(e) => updateInvoiceLine(idx, 'cost', e.target.value)} className="h-8 text-right" data-testid={`invoice-cost-${idx}`} />
                    </td>
                    <td>
                      <Input type="number" value={l.weight} placeholder="0" disabled={readOnly}
                        onChange={(e) => updateInvoiceLine(idx, 'weight', e.target.value)} className="h-8 text-right" data-testid={`invoice-weight-${idx}`} />
                    </td>
                    <td className="text-right font-mono">{fmt((Number(l.price) || 0) * (Number(l.weight) || 0))}</td>
                    {!readOnly && (
                      <td>
                        <Button variant="ghost" size="sm" className="text-red-500 h-8 px-2" onClick={() => removeInvoiceLine(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border font-semibold">
                  <td colSpan={6} className="text-right pr-4">Total Facture</td>
                  <td className="text-right font-mono text-primary" data-testid="total-facture">{fmt(totalFacture)}</td>
                  {!readOnly && <td></td>}
                </tr>
                <tr className="font-semibold">
                  <td colSpan={6} className="text-right pr-4 text-muted-foreground">Total Coût</td>
                  <td className="text-right font-mono text-amber-600 dark:text-amber-500" data-testid="total-cost">{fmt(totalCost)}</td>
                  {!readOnly && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Expenses */}
      <Card>
        <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2"><Receipt className="w-4 h-4 text-primary" /> Expenses (Dépenses)</CardTitle>
          {!readOnly && (
            <Button variant="outline" size="sm" onClick={addExpenseLine} data-testid="add-expense-line-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add line</Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th className="w-8">#</th>
                  <th style={{ minWidth: 200 }}>Description</th>
                  <th style={{ minWidth: 200 }}>Débit (6311)</th>
                  <th style={{ minWidth: 200 }}>Crédit (5311)</th>
                  <th className="text-right" style={{ minWidth: 130 }}>Montant (XOF)</th>
                  {!readOnly && <th className="w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {expenseLines.map((l, idx) => (
                  <tr key={idx} data-testid={`expense-line-${idx}`}>
                    <td className="text-muted-foreground text-xs">{idx + 1}</td>
                    <td>
                      <Input value={l.description} placeholder="e.g. DIESEL, GLACE..." disabled={readOnly}
                        onChange={(e) => updateExpenseLine(idx, 'description', e.target.value)} className="h-8" data-testid={`expense-desc-${idx}`} />
                    </td>
                    <td>
                      <SearchableSelect
                        size="sm"
                        disabled={readOnly}
                        value={l.debit_account_code}
                        onChange={(v) => updateExpenseLine(idx, 'debit_account_code', v)}
                        placeholder="Débit account"
                        testid={`expense-debit-${idx}`}
                        options={expenseDebitAccounts.map(a => ({ value: a.code, label: `${a.code} - ${a.name}` }))}
                      />
                    </td>
                    <td>
                      <SearchableSelect
                        size="sm"
                        disabled={readOnly}
                        value={l.credit_account_code}
                        onChange={(v) => updateExpenseLine(idx, 'credit_account_code', v)}
                        placeholder="Crédit account"
                        testid={`expense-credit-${idx}`}
                        options={cashAccounts.map(a => ({ value: a.code, label: `${a.code} - ${a.name}` }))}
                      />
                    </td>
                    <td>
                      <Input type="number" value={l.amount} placeholder="0" disabled={readOnly}
                        onChange={(e) => updateExpenseLine(idx, 'amount', e.target.value)} className="h-8 text-right" data-testid={`expense-amount-${idx}`} />
                    </td>
                    {!readOnly && (
                      <td>
                        <Button variant="ghost" size="sm" className="text-red-500 h-8 px-2" onClick={() => removeExpenseLine(idx)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border font-semibold">
                  <td colSpan={4} className="text-right pr-4">Total Dépenses</td>
                  <td className="text-right font-mono text-primary" data-testid="total-depenses">{fmt(totalDepenses)}</td>
                  {!readOnly && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Result */}
      <Card className="border-primary/30">
        <CardContent className="py-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center sm:text-left">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Facture</p>
              <p className="text-lg font-bold font-mono">{fmt(totalFacture)} <span className="text-xs text-muted-foreground">XOF</span></p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Coût</p>
              <p className="text-lg font-bold font-mono text-amber-600 dark:text-amber-500" data-testid="summary-cost">{fmt(totalCost)} <span className="text-xs text-muted-foreground">XOF</span></p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Dépenses</p>
              <p className="text-lg font-bold font-mono">{fmt(totalDepenses)} <span className="text-xs text-muted-foreground">XOF</span></p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1 justify-center sm:justify-start"><TrendingUp className="w-3 h-3" /> Résultat</p>
              <p className={`text-lg font-bold font-mono ${result >= 0 ? 'text-emerald-500' : 'text-red-500'}`} data-testid="trip-result">{fmt(result)} <span className="text-xs text-muted-foreground">XOF</span></p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Ship Dialog */}
      <Dialog open={shipDialogOpen} onOpenChange={setShipDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Ship</DialogTitle>
            <DialogDescription>Creates a class-9 account with an auto code (9011…).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Ship Name *</Label>
            <Input value={newShipName} onChange={(e) => setNewShipName(e.target.value)} placeholder="e.g. OC2" autoFocus data-testid="new-ship-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShipDialogOpen(false)}>Cancel</Button>
            <Button onClick={createShip} data-testid="create-ship-btn">Create Ship</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FishingTripPage;
